/*!
 * JM Language Render v3.4 — multi-name necklace preview in 31 scripts, built to the supplier's 5-name Arabic necklace.
 * JMLangRender.draw(canvas, baseImage, chainPts, plates, metal, opts) → Promise<placed[]>
 *   chainPts: [[x, y], …] the chain's centre line, measured on a photo opts.w (1500) wide
 *   plates:   [{ text: 'مريم', lang: 'Arabic' }, …] left to right along the chain
 *   metal:    'gold' | 'silver' | 'rose'
 *   opts: { w: 1500, letter: 82 (alef height in photo px), weight: 0.03 (extra stroke / letter),
 *           slots: { "2": [[centre, widest], …] } (share of the chain's length from its lowest point / photo px) }
 * Blueprint (custom-language-blueprint.png):
 *   - every name is the same size whatever the number of names; a round eyelet with a hole at each end,
 *     about half-way up the letters; the chain runs through the eyelets
 *   - places for 1–5 names copied from the supplier photo; each name turns with the chain
 *   - satin gold: flat brushed face, thin darker rim, soft shadow
 */
(function () {
  'use strict';
  // our own fonts, uploaded to Content → Files (English: JM Signature, JM's own script); if one can't load, its stand-in is used
  var OWN = { 'JM Signature': ['/cdn/shop/files/jm-signature.woff', 'Pinyon Script'] };
  // language → [Google font family, weight, right-to-left, how the letters are held together, sample, size (optional), tracking (em, optional)]
  //   join: letters connect along one line (Arabic baseline, the Hindi top line, joined script) — gaps on that line are bridged
  //   touch: letters stand apart — they are pulled together until they touch, as the workshop cuts Hebrew / Greek / Korean names
  //   size: block scripts (Hebrew, Korean, Chinese, Cyrillic…) look bigger than other scripts at the same height, so they are
  //   drawn a little smaller and every name on one piece reads as the same size
  var L = {
    'Arabic': ['Lateef', 400, 1, 'join', 'مريم'],
    'Amharic': ['Noto Sans Ethiopic', 600, 0, 'touch', 'ሶፊያ'],
    'Aramaic (Assyrian)': ['Noto Sans Syriac Eastern', 600, 1, 'touch', 'ܣܘܦܝܐ', 0.88],
    'Armenian': ['Noto Sans Armenian', 600, 0, 'touch', 'Սոֆյա', 0.88],
    'Baybayin (Filipino)': ['Noto Sans Tagalog', 400, 0, 'touch', 'ᜐᜓᜉᜒᜌ'],
    'Bengali': ['Noto Sans Bengali', 600, 0, 'join', 'সোফিয়া', 0.88],
    'English': ['JM Signature', 400, 0, 'touch', 'Sophia', 1, -0.12],
    'Farsi (Persian)': ['Lateef', 400, 1, 'join', 'سوفیا'],
    'Georgian': ['Noto Sans Georgian', 600, 0, 'touch', 'სოფია'],
    'Greek': ['Noto Sans', 600, 0, 'touch', 'Σοφία', 0.88],
    'Gujarati': ['Noto Sans Gujarati', 600, 0, 'touch', 'સોફિયા'],
    'Hebrew': ['Noto Sans Hebrew', 600, 1, 'touch', 'סופיה', 0.78],
    'Hindi': ['Noto Sans Devanagari', 600, 0, 'join', 'सोफ़िया', 0.88],
    'Japanese (Hiragana)': ['Noto Sans JP', 600, 0, 'touch', 'そふぃあ', 0.9],
    'Japanese (Kanji)': ['Noto Sans JP', 600, 0, 'touch', '桜', 0.9],
    'Japanese (Katakana)': ['Noto Sans JP', 600, 0, 'touch', 'ソフィア', 0.9],
    'Kannada': ['Noto Sans Kannada', 600, 0, 'touch', 'ಸೋಫಿಯಾ'],
    'Korean': ['Noto Sans KR', 600, 0, 'touch', '소피아', 0.82],
    'Khmer': ['Noto Sans Khmer', 600, 0, 'touch', 'សុភា'],
    'Malayalam': ['Noto Sans Malayalam', 600, 0, 'touch', 'സോഫിയ', 0.88],
    'Mandarin (Modern)': ['Noto Sans SC', 600, 0, 'touch', '索菲亚', 0.82],
    'Mandarin (Traditional)': ['Noto Sans TC', 600, 0, 'touch', '索菲亞', 0.82],
    'Punjabi': ['Noto Sans Gurmukhi', 600, 0, 'join', 'ਸੋਫੀਆ', 0.92],
    'Russian': ['Noto Sans', 600, 0, 'touch', 'София', 0.85],
    'Tamil': ['Noto Sans Tamil', 600, 0, 'touch', 'சோபியா', 0.85],
    'Telugu': ['Noto Sans Telugu', 600, 0, 'touch', 'సోఫియా'],
    'Thai': ['Noto Sans Thai', 600, 0, 'touch', 'โซเฟีย'],
    'Tibetan': ['Noto Serif Tibetan', 600, 0, 'join', 'བསོད་ནམས'],
    'Tigrinya': ['Noto Sans Ethiopic', 600, 0, 'touch', 'ሶፊያ'],
    'Ukrainian': ['Noto Sans', 600, 0, 'touch', 'Софія', 0.85],
    'Urdu': ['Lateef', 400, 1, 'join', 'صوفیہ']
  };
  function arabicLike(I) { return I[0] === 'Lateef'; }
  function info(lang) {
    if (L[lang]) return L[lang];
    var k = Object.keys(L).filter(function (n) { return String(lang || '').toLowerCase().indexOf(n.split(' ')[0].toLowerCase()) === 0; })[0];
    return L[k || 'English'];
  }

  // Google Fonts, the whole family (split by Google into small pieces the browser fetches only as letters need them),
  // so joined scripts keep their joining forms
  var fonts = {};
  function loadFont(fam, wt, text) {
    var key = fam + '|' + wt;
    if (OWN[fam]) {
      if (!fonts[key]) fonts[key] = (window.FontFace ? new FontFace(fam, 'url(' + OWN[fam][0] + ')', { weight: String(wt) }).load() : Promise.reject())
        .then(function (ff) { document.fonts.add(ff); return fam; }, function () { return loadFont(OWN[fam][1], wt, text); });
      return fonts[key];
    }
    if (!fonts[key]) fonts[key] = document.fonts.load(wt + ' 40px "' + fam + '"', text).then(function (f) {
      if (f && f.length) return fam;
      var alias = 'JMLang' + Object.keys(fonts).length;
      return fetch('https://fonts.googleapis.com/css2?family=' + encodeURIComponent(fam).replace(/%20/g, '+') + ':wght@' + wt + '&display=block')
        .then(function (r) { return r.text(); })
        .then(function (css) {
          css.split('@font-face').slice(1).forEach(function (blk) {
            var u = (blk.match(/url\(([^)]+)\)/) || [])[1], r = (blk.match(/unicode-range:\s*([^;]+);/) || [])[1];
            if (!u) return;
            var desc = { weight: String(wt) }; if (r) desc.unicodeRange = r.trim();
            document.fonts.add(new FontFace(alias, 'url(' + u + ')', desc));
          });
          return alias;
        });
    }).catch(function () { return fam; });
    return fonts[key].then(function (f) { return document.fonts.load(wt + ' 40px "' + f + '"', text).then(function () { return f; }, function () { return f; }); });
  }

  /* ----- satin metal ----- */
  var PAL = {   // rim, face, light — matched to the supplier's plates (on #f2f2f2)
    gold:   [[176, 142, 82], [222, 187, 124], [236, 206, 150]],
    silver: [[150, 152, 158], [205, 207, 212], [226, 228, 232]],
    rose:   [[176, 124, 104], [224, 172, 150], [238, 200, 182]]
  };
  function mix(a, b, t) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]; }
  function boxBlur(src, w, h, r) {
    var tmp = new Float32Array(w * h), out = new Float32Array(w * h), x, y, acc, n = 2 * r + 1;
    for (y = 0; y < h; y++) { acc = 0; for (x = -r; x <= r; x++) acc += src[y * w + Math.min(w - 1, Math.max(0, x))];
      for (x = 0; x < w; x++) { tmp[y * w + x] = acc / n; acc += src[y * w + Math.min(w - 1, x + r + 1)] - src[y * w + Math.max(0, x - r)]; } }
    for (x = 0; x < w; x++) { acc = 0; for (y = -r; y <= r; y++) acc += tmp[Math.min(h - 1, Math.max(0, y)) * w + x];
      for (y = 0; y < h; y++) { out[y * w + x] = acc / n; acc += tmp[Math.min(h - 1, y + r + 1) * w + x] - tmp[Math.max(0, y - r) * w + x]; } }
    return out;
  }
  function seeded(i) { var x = Math.sin(i * 12.9898) * 43758.5453; return x - Math.floor(x); }
  // alpha mask → satin metal: flat brushed face, slightly lighter towards the top left, darker thin rim
  function metalise(m, metal, rim) {
    var P = PAL[metal] || PAL.gold, w = m.width, h = m.height;
    var src = m.getContext('2d').getImageData(0, 0, w, h).data, A = new Float32Array(w * h), i;
    for (i = 0; i < w * h; i++) A[i] = src[i * 4 + 3] / 255;
    var B = boxBlur(A, w, h, Math.max(1, Math.round(rim)));
    var c = document.createElement('canvas'); c.width = w; c.height = h;
    var cx = c.getContext('2d'), img = cx.createImageData(w, h), d = img.data;
    for (var y = 0; y < h; y++) {
      var streak = (seeded(y) - 0.5) * 0.06;                       // fine horizontal brushing
      for (var x = 0; x < w; x++) {
        i = y * w + x; var a = A[i]; if (a <= 0.01) continue;
        var edge = Math.max(0, Math.min(1, (1 - B[i]) * 2.2));      // 0 inside the stroke, 1 at its edge
        var lit = 0.5 - 0.35 * (y / h) + 0.15 * (1 - x / w);         // soft studio light from the top left
        var col = mix(P[1], P[2], Math.max(0, Math.min(1, lit + streak)));
        // lit upper edges catch a little light, lower edges darken — reads as a cut metal edge
        var up = (y > 0 && x > 0) ? (A[i - w] + A[i - 1]) / 2 : 1, dn = (y < h - 1 && x < w - 1) ? (A[i + w] + A[i + 1]) / 2 : 1;
        if (edge > 0) col = up < dn ? mix(col, P[2], edge * 0.6) : mix(col, P[0], edge * 0.75);
        d[i * 4] = col[0]; d[i * 4 + 1] = col[1]; d[i * 4 + 2] = col[2]; d[i * 4 + 3] = a * 255;
      }
    }
    cx.putImageData(img, 0, 0);
    return c;
  }

  /* ----- one name → plate mask (thickened letters + eyelets) ----- */
  var alefCache = {};
  function alefHeight(fam, wt) {   // ink height of an alef at 100px, to size every font by its letters
    var k = fam + wt; if (alefCache[k]) return alefCache[k];
    var c = document.createElement('canvas'); c.width = 200; c.height = 260; var x = c.getContext('2d');
    x.font = wt + ' 100px "' + fam + '", serif'; x.fillStyle = '#000'; x.fillText('ا', 80, 180);
    var d = x.getImageData(0, 0, 200, 260).data, y0 = 260, y1 = -1;
    for (var y = 0; y < 260; y++) for (var xx = 0; xx < 200; xx++) if (d[(y * 200 + xx) * 4 + 3] > 100) { if (y < y0) y0 = y; if (y > y1) y1 = y; }
    var v = y1 > y0 ? y1 - y0 + 1 : 70;
    if (document.fonts.check(wt + ' 40px "' + fam + '"', 'ا')) alefCache[k] = v;   // only remember it once the real font is in
    return v;
  }
  var sampleCache = {};
  // how big a script looks: the geometric mean of its full ink height and the band that holds the middle 80% of its ink
  // (Latin and Georgian have tall ascenders and descenders around a small body; Hebrew, Korean and Chinese fill the whole
  // height), so every name on one piece reads as the same size
  function sampleHeight(fam, wt, sample) {
    var k = fam + wt + sample; if (sampleCache[k]) return sampleCache[k];
    var c = document.createElement('canvas'); c.width = 900; c.height = 320; var x = c.getContext('2d');
    x.font = wt + ' 100px "' + fam + '", serif'; x.fillStyle = '#000'; x.fillText(sample, 20, 210);
    var d = x.getImageData(0, 0, 900, 320).data, rows = new Array(320), tot = 0, y0 = -1, y1 = -1, y, xx;
    for (y = 0; y < 320; y++) { rows[y] = 0; for (xx = 0; xx < 900; xx++) if (d[(y * 900 + xx) * 4 + 3] > 100) rows[y]++; tot += rows[y]; if (rows[y]) { if (y0 < 0) y0 = y; y1 = y; } }
    var v = 79;
    if (tot) { var acc = 0, a = -1, b = -1; for (y = 0; y < 320; y++) { acc += rows[y]; if (a < 0 && acc >= tot * 0.1) a = y; if (b < 0 && acc >= tot * 0.9) b = y; }
      v = Math.sqrt((y1 - y0 + 1) * (b - a + 1)); }
    if (document.fonts.check(wt + ' 40px "' + fam + '"', sample)) sampleCache[k] = v;
    return v;
  }
  function plateMask(text, lang, letter, weight, fam) {
    var I = info(lang), SS = 2, LH = letter * SS, AR = arabicLike(I);
    var F = Math.round(AR ? 100 * LH / alefHeight(fam, I[1]) : 100 * LH * 0.8 * (I[5] || 1) / sampleHeight(fam, I[1], I[4]));
    var c = document.createElement('canvas'), x = c.getContext('2d');
    var font = I[1] + ' ' + F + 'px "' + fam + '", serif';
    var track = I[6] && fam === I[0] ? Math.round(I[6] * F) + 'px' : '0px';   // tighter letter spacing for scripts set loosely (JM Signature)
    x.font = font; x.letterSpacing = track; var tw = Math.ceil(x.measureText(text).width);
    c.width = tw + Math.round(LH * 2.4); c.height = Math.round(LH * 3.6);
    var base = Math.round(LH * 2.2), tx = c.width - Math.round(LH * 1.2);
    x.font = font; x.letterSpacing = track; x.fillStyle = '#fff'; x.strokeStyle = '#fff'; x.lineJoin = 'round'; x.lineCap = 'round';
    x.direction = I[2] ? 'rtl' : 'ltr'; x.textAlign = I[2] ? 'right' : 'left'; if (!I[2]) tx = Math.round(LH * 1.2);
    x.lineWidth = LH * weight; x.fillText(text, tx, base); x.strokeText(text, tx, base);
    var W = c.width, H = c.height, d = x.getImageData(0, 0, W, H).data, x0 = W, x1 = -1, y0 = H, y1 = -1, xx, yy;
    function ink(px, py) { return d[(py * W + px) * 4 + 3] > 110; }
    for (yy = 0; yy < H; yy++) for (xx = 0; xx < W; xx++) if (ink(xx, yy)) { if (xx < x0) x0 = xx; if (xx > x1) x1 = xx; if (yy < y0) y0 = yy; if (yy > y1) y1 = yy; }
    if (x1 < 0) return null;
    var bar = Math.max(3, LH * weight * 0.9 + LH * 0.05), join = base;
    if (I[3] === 'join') {
      // the joining line: the row with the most ink (Arabic: near the baseline; Hindi: the top line)
      var best = -1, ya = AR ? Math.round(base - LH * 0.35) : y0, yb = AR ? Math.round(base + LH * 0.05) : y1;
      for (yy = ya; yy <= yb; yy++) { var n = 0; for (xx = x0; xx <= x1; xx++) if (ink(xx, yy)) n++; if (n > best) { best = n; join = yy; } }
      // keep the name one piece: bridge any gap along that line (letters that don't connect)
      var run = -1, seen = false;
      for (xx = x0; xx <= x1; xx++) {
        var on = ink(xx, Math.round(join)) || ink(xx, Math.round(join - bar / 2)) || ink(xx, Math.round(join + bar / 2));
        if (on) { if (run >= 0 && seen) x.fillRect(run - 1, join - bar / 2, xx - run + 2, bar); run = -1; seen = true; }
        else if (run < 0) run = xx;
      }
    } else {
      // letters that stand apart are pulled together until they touch, the way the workshop cuts them (no bar)
      var cols = new Uint8Array(W), blocks = [], s0 = -1;
      for (xx = x0; xx <= x1; xx++) for (yy = y0; yy <= y1; yy++) if (ink(xx, yy)) { cols[xx] = 1; break; }
      for (xx = x0; xx <= x1 + 1; xx++) { var lit = xx <= x1 && cols[xx]; if (lit && s0 < 0) s0 = xx; else if (!lit && s0 >= 0) { blocks.push([s0, xx - 1]); s0 = -1; } }
      if (blocks.length > 1) {
        var ov = Math.max(2, LH * (weight + 0.04)), tmp = document.createElement('canvas'); tmp.width = W; tmp.height = H;
        tmp.getContext('2d').drawImage(c, 0, 0); x.clearRect(0, 0, W, H);
        var shift = 0, prev = blocks[0][1];
        blocks.forEach(function (b, k) { if (k > 0) { shift += (b[0] - prev - 1) + ov; prev = b[1]; } x.drawImage(tmp, b[0], 0, b[1] - b[0] + 1, H, b[0] - shift, 0, b[1] - b[0] + 1, H); });
        d = x.getImageData(0, 0, W, H).data; x0 = W; x1 = -1;
        for (yy = y0; yy <= y1; yy++) for (xx = 0; xx < W; xx++) if (ink(xx, yy)) { if (xx < x0) x0 = xx; if (xx > x1) x1 = xx; }
      }
      join = base; bar = 0;
    }
    d = x.getImageData(0, 0, W, H).data;
    // eyelets: round loops with a hole, half-way up the letters, touching each end of the name
    var eyeY = AR ? join - LH * 0.5 : (y0 + base) / 2, R = LH * 0.15, hole = R * 0.45, eyes = [];
    [-1, 1].forEach(function (side) {
      var ex = side < 0 ? x0 - R * 0.35 : x1 + R * 0.35;
      // weld the eyelet to the nearest bit of letter (a short arm, like the jeweller's solder joint)
      var bx = ex, by = eyeY, bd = 1e9, reach = Math.round(LH * 0.9);
      for (var py = Math.max(0, Math.round(eyeY - reach)); py <= Math.min(H - 1, Math.round(eyeY + reach)); py += 2)
        for (var px = Math.max(0, Math.round(ex - reach)); px <= Math.min(W - 1, Math.round(ex + reach)); px += 2)
          if (ink(px, py)) { var dd = (px - ex) * (px - ex) + (py - eyeY) * (py - eyeY) * 1.6; if (dd < bd) { bd = dd; bx = px; by = py; } }
      x.lineWidth = R * 1.05; x.beginPath(); x.moveTo(ex, eyeY); x.lineTo(bx - side * R * 0.2, by); x.stroke();
      x.beginPath(); x.arc(ex, eyeY, R, 0, Math.PI * 2); x.fill();
      eyes.push(ex);
    });
    // one piece of metal: every dot, hamza or loose letter is joined to the rest, as the workshop cuts it
    joinParts(x, W, H, Math.max(3, LH * 0.075));
    x.globalCompositeOperation = 'destination-out';
    eyes.forEach(function (ex) { x.beginPath(); x.arc(ex, eyeY, hole, 0, Math.PI * 2); x.fill(); });
    x.globalCompositeOperation = 'source-over';
    var pad = Math.round(LH * 0.15), left = Math.min(eyes[0], eyes[1]) - R - pad, right = Math.max(eyes[0], eyes[1]) + R + pad;
    var top = Math.min(y0, eyeY - R) - pad, bot = Math.max(y1, join + bar) + pad;
    var out = document.createElement('canvas'); out.width = Math.round(right - left); out.height = Math.round(bot - top);
    out.getContext('2d').drawImage(c, -left, -top);
    return { c: out, ss: SS, w: out.width / SS, h: out.height / SS,
      eyeL: [(Math.min(eyes[0], eyes[1]) - left) / SS, (eyeY - top) / SS], eyeR: [(Math.max(eyes[0], eyes[1]) - left) / SS, (eyeY - top) / SS],
      span: Math.abs(eyes[1] - eyes[0]) / SS, R: R / SS, hole: hole / SS, rim: Math.max(1, LH * 0.02) };
  }

  // join every separate piece of the mask to the main piece with a short bridge (closest piece first)
  function joinParts(x, W, H, width) {
    var d = x.getImageData(0, 0, W, H).data, N = W * H, lab = new Int32Array(N), comps = [], i, k;
    for (i = 0; i < N; i++) {
      if (lab[i] || d[i * 4 + 3] <= 110) continue;
      var id = comps.length + 1, stack = [i], px = [];
      lab[i] = id;
      while (stack.length) {
        var q = stack.pop(); px.push(q); var qx = q % W, qy = (q - qx) / W;
        for (var dy = -1; dy <= 1; dy++) for (var dx = -1; dx <= 1; dx++) {
          var nx = qx + dx, ny = qy + dy; if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
          var nq = ny * W + nx; if (!lab[nq] && d[nq * 4 + 3] > 110) { lab[nq] = id; stack.push(nq); }
        }
      }
      comps.push(px);
    }
    if (comps.length < 2) return;
    comps.sort(function (a, b) { return b.length - a.length; });
    // edge pixels of each piece, enough to find the closest points
    function edge(px) { var out = []; for (var j = 0; j < px.length; j++) { var q = px[j], qx = q % W, qy = (q - qx) / W; if (qx < 1 || qy < 1 || qx > W - 2 || qy > H - 2 || d[(q - 1) * 4 + 3] <= 110 || d[(q + 1) * 4 + 3] <= 110 || d[(q - W) * 4 + 3] <= 110 || d[(q + W) * 4 + 3] <= 110) out.push([qx, qy]); } return out; }
    var main = edge(comps[0]), rest = comps.slice(1).map(edge);
    x.lineCap = 'round'; x.lineWidth = width;
    while (rest.length) {
      var best = 1e18, bi = -1, ba = null, bb = null;
      for (k = 0; k < rest.length; k++) {
        var e = rest[k];
        for (var a = 0; a < e.length; a += 2) for (var b = 0; b < main.length; b += 2) {
          var ddx = e[a][0] - main[b][0], ddy = e[a][1] - main[b][1], dd = ddx * ddx + ddy * ddy;
          if (dd < best) { best = dd; bi = k; ba = e[a]; bb = main[b]; }
        }
      }
      // a short stem from the piece into the letter, ending inside both
      var ux = bb[0] - ba[0], uy = bb[1] - ba[1], ul = Math.hypot(ux, uy) || 1, inset = width * 0.6;
      x.beginPath(); x.moveTo(ba[0] - ux / ul * inset, ba[1] - uy / ul * inset); x.lineTo(bb[0] + ux / ul * inset, bb[1] + uy / ul * inset); x.stroke();
      main = main.concat(rest[bi]); rest.splice(bi, 1);
    }
  }

  /* ----- chain path ----- */
  function path(pts) { var s = [0]; for (var i = 1; i < pts.length; i++) s.push(s[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1])); return { pts: pts, s: s, len: s[s.length - 1] }; }
  function at(P, s) { s = Math.max(0, Math.min(P.len, s)); var i = 1; while (i < P.s.length - 1 && P.s[i] < s) i++; var t = (s - P.s[i - 1]) / Math.max(1e-6, P.s[i] - P.s[i - 1]); return [P.pts[i - 1][0] + (P.pts[i][0] - P.pts[i - 1][0]) * t, P.pts[i - 1][1] + (P.pts[i][1] - P.pts[i - 1][1]) * t]; }
  function chordTo(P, s0, d) { var p0 = at(P, s0), lo = s0, hi = Math.min(P.len, s0 + d * 3); for (var k = 0; k < 30; k++) { var mid = (lo + hi) / 2, p = at(P, mid); if (Math.hypot(p[0] - p0[0], p[1] - p0[1]) < d) lo = mid; else hi = mid; } return hi; }
  function lowest(P) { var b = 0; for (var i = 1; i < P.pts.length; i++) if (P.pts[i][1] > P.pts[b][1]) b = i; return P.s[b]; }

  // places for 1–5 names (share of the chain's length from its lowest point), measured on the supplier photo
  var SLOTS = { '1': [0], '2': [-0.0675, 0.0675], '3': [-0.135, 0, 0.135], '4': [-0.2, -0.0675, 0.0675, 0.2], '5': [-0.267, -0.135, 0, 0.135, 0.267] };
  var WIDEST = { '1': 480, '2': 340, '3': 340, '4': 330, '5': 320 };   // eyelet to eyelet, photo px

  function draw(cv, base, chainPts, plates, metal, opts) {
    var o = opts || {};
    var W = base.naturalWidth || base.width, H = base.naturalHeight || base.height, k = W / (o.w || 1500);
    var list = (plates || []).filter(function (p) { return p && String(p.text || '').trim(); }), n = list.length;
    return Promise.all(list.map(function (p) { var I = info(p.lang); return loadFont(I[0], I[1], String(p.text).trim() + (arabicLike(I) ? 'ا' : I[4])); })).then(function (fams) {
      cv.width = W; cv.height = H;
      var ctx = cv.getContext('2d'); ctx.drawImage(base, 0, 0, W, H);
      if (!n) return [];
      var P = path(chainPts.map(function (q) { return [q[0] * k, q[1] * k]; })), mid = lowest(P);
      var slots = (o.slots || SLOTS)[String(Math.min(n, 5))] || SLOTS['5'];
      var letter = (o.letter || 82) * k, weight = o.weight == null ? 0.03 : o.weight;
      var masks = list.map(function (p, i) { return plateMask(String(p.text).trim(), p.lang, letter, weight, fams[i]); });
      // one letter size for every name; only a name too long for its place makes them all a little smaller
      var widest = (o.widest || WIDEST)[String(Math.min(n, 5))] * k, sc = 1;
      masks.forEach(function (m) { if (m && m.span > widest) sc = Math.min(sc, widest / m.span); });
      var hw = (o.hw || 6) * k, placed = [];
      masks.forEach(function (m, i) {
        if (!m) return;
        var span = m.span * sc, c = mid + (slots[i] != null ? slots[i] : 0) * P.len;
        var s0 = c - span / 2, s1 = chordTo(P, s0, span);
        placed.push({ m: m, s0: s0, s1: s1, a: at(P, s0), b: at(P, s1), sc: sc });
      });
      // the chain stops at each eyelet; between them the name takes its place
      ctx.save(); ctx.fillStyle = '#f2f2f2';
      placed.forEach(function (p) { var r = p.m.hole * p.sc; for (var t = p.s0 + r; t <= p.s1 - r; t += 1.5) { var q = at(P, t); ctx.beginPath(); ctx.arc(q[0], q[1], hw * 1.9, 0, Math.PI * 2); ctx.fill(); } });
      ctx.restore();
      placed.forEach(function (p) {
        var m = p.m, s = p.sc, ang = Math.atan2(p.b[1] - p.a[1], p.b[0] - p.a[0]);
        var face = metalise(m.c, metal, m.rim * m.ss);
        var dw = m.w * s, dh = m.h * s, ox = -m.eyeL[0] * s, oy = -m.eyeL[1] * s;
        ctx.save(); ctx.translate(p.a[0], p.a[1]); ctx.rotate(ang);
        ctx.save(); ctx.filter = 'blur(' + (3.5 * k).toFixed(1) + 'px)'; ctx.globalAlpha = 0.2;
        ctx.drawImage(tint(m.c, '#4a3a20'), ox + 2 * k, oy + 5 * k, dw, dh); ctx.restore();
        ctx.drawImage(tint(m.c, 'rgb(' + (PAL[metal] || PAL.gold)[0].join(',') + ')'), ox + 0.8 * k, oy + 1.4 * k, dw, dh);   // edge thickness
        ctx.drawImage(face, ox, oy, dw, dh);
        ctx.restore();
        p.ang = ang;
        // where the plate lands on the photo (for the thumbnail and the floating preview)
        var ca = Math.cos(ang), sa = Math.sin(ang), bx0 = 1e9, by0 = 1e9, bx1 = -1e9, by1 = -1e9;
        [[ox, oy], [ox + dw, oy], [ox, oy + dh], [ox + dw, oy + dh]].forEach(function (q) { var X = p.a[0] + q[0] * ca - q[1] * sa, Y = p.a[1] + q[0] * sa + q[1] * ca; bx0 = Math.min(bx0, X); by0 = Math.min(by0, Y); bx1 = Math.max(bx1, X); by1 = Math.max(by1, Y); });
        p.box = [bx0, by0, bx1, by1];
      });
      return placed;
    });
  }
  function tint(src, colour) { var c = document.createElement('canvas'); c.width = src.width; c.height = src.height; var x = c.getContext('2d'); x.drawImage(src, 0, 0); x.globalCompositeOperation = 'source-in'; x.fillStyle = colour; x.fillRect(0, 0, c.width, c.height); return c; }

  window.JMLangRender = { draw: draw, info: info, languages: L, loadFont: loadFont, version: 3 };
})();
